import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CarouselItemType {
    altText?: string;
    caption?: string;
    key?: number;
    src: string;
    display_level?: number;
    link_url?: string;
    title?: string | null;
    description?: string | null;
    data?: any;
}

interface ImageDimensions {
    width: number;
    height: number;
    aspectRatio: number;
}

interface AmCarouselProps {
    interval?: number;
    items: CarouselItemType[];
    style?: React.CSSProperties;
    heightRatio?: number;
    objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
    showControls?: boolean;
    showIndicators?: boolean;
    useImageRatio?: boolean;
}

export default function AmCarousel({
    interval = 5000,
    items = [],
    style = {},
    heightRatio = 1,
    objectFit = "contain",
    showControls = true,
    showIndicators = true,
    useImageRatio = true,
}: AmCarouselProps) {
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(null);
    const [isHovered, setIsHovered] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadImage = useCallback((url: string) => {
        const img = new Image();
        img.onload = () => {
            setImageDimensions({
                width: img.width,
                height: img.height,
                aspectRatio: img.height / img.width
            });
        };
        img.src = url;
    }, []);

    useEffect(() => {
        if (items.length > 0) {
            // fix dimension of first image
            loadImage(items[0].src);
        }
    }, [items, loadImage]);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.clientWidth);
            }
        };

        const observer = new ResizeObserver(updateWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
            updateWidth();
        }

        window.addEventListener('resize', updateWidth);

        return () => {
            if (containerRef.current) observer.unobserve(containerRef.current);
            window.removeEventListener('resize', updateWidth);
            stopInterval();
        };
    }, []);

    const startInterval = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            if (!isHovered) {
                setActiveIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
            }
        }, interval);
    }, [items.length, interval, isHovered]);

    const stopInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (items.length > 1) {
            startInterval();
        }
        return () => stopInterval();
    }, [startInterval, stopInterval, items.length]);

    const imageStyle = useMemo((): React.CSSProperties => {
        const _width = containerWidth;
        const calculatedRatio = useImageRatio && imageDimensions?.width ? imageDimensions.height / imageDimensions.width : heightRatio;
        return {
            width: `${_width}px`,
            height: `${_width * calculatedRatio}px`,
            objectFit: objectFit
        };
    }, [containerWidth, heightRatio, objectFit, imageDimensions, useImageRatio]);

    const next = (): void => {
        const nextIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
        setActiveIndex(nextIndex);
        startInterval(); // Reset timer on manual interaction
    };

    const previous = (): void => {
        const nextIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
        setActiveIndex(nextIndex);
        startInterval(); // Reset timer on manual interaction
    };

    const goToIndex = (newIndex: number): void => {
        setActiveIndex(newIndex);
        startInterval(); // Reset timer on manual interaction
    };

    if (!items || items.length === 0) return null;

    // Single Item Rendering
    if (items.length === 1) {
        return (
            <div ref={containerRef} style={{ ...style, position: "relative", overflow: "hidden" }}>
                {items[0].link_url ? (
                    <a href={items[0].link_url} target="_blank" rel="noreferrer">
                        <img src={items[0].src} alt={items[0].altText} style={imageStyle} />
                    </a>
                ) : (
                    <img src={items[0].src} alt={items[0].altText} style={imageStyle} />
                )}
                {items[0].title && (
                    <div style={{ position: "absolute", bottom: "0", left: "0", right: "0", backgroundColor: "rgba(0, 0, 0, 0.5)", color: "white", padding: "16px 14px" }}>
                        {items[0].link_url ? (
                            <a href={items[0].link_url} target="_blank" rel="noreferrer">
                                <h5 style={{ textAlign: "center", color: "white", marginBottom: "4px" }}>{items[0].title}</h5>
                            </a>
                        ) : (
                            <h5 style={{ textAlign: "center", color: "white", marginBottom: "4px" }}>{items[0].title}</h5>
                        )}
                        {items[0].description && <div style={{ textAlign: "center", fontSize: "14px" }}>{items[0].description}</div>}
                    </div>
                )}
            </div>
        );
    }

    // Carousel Rendering
    return (
        <div
            ref={containerRef}
            style={{ ...style, position: "relative", overflow: "hidden" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
                {items.map((item, index) => (
                    <div key={index} className="flex-shrink-0 w-full relative">
                        {item.link_url ? (
                            <a href={item.link_url} target="_blank" rel="noreferrer" className="block w-full">
                                <img src={item.src} alt={item.altText} style={imageStyle} className="w-full" />
                            </a>
                        ) : (
                            <img src={item.src} alt={item.altText} style={imageStyle} className="w-full" />
                        )}

                        {item.title && (
                            <div style={{ position: "absolute", bottom: "0", left: "0", right: "0", backgroundColor: "rgba(0, 0, 0, 0.5)", color: "white", padding: "16px 14px 50px 14px" }}>
                                {item.link_url ? (
                                    <a href={item.link_url} target="_blank" rel="noreferrer">
                                        <h5 style={{ textAlign: "center", color: "white", marginBottom: "4px" }}>{item.title}</h5>
                                    </a>
                                ) : (
                                    <h5 style={{ textAlign: "center", color: "white", marginBottom: "4px" }}>{item.title}</h5>
                                )}
                                {item.description && <div style={{ textAlign: "center", fontSize: "14px" }}>{item.description}</div>}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Controls */}
            {showControls && (
                <>
                    <button
                        onClick={previous}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full transition-colors z-10"
                        aria-label="Previous slide"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    <button
                        onClick={next}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full transition-colors z-10"
                        aria-label="Next slide"
                    >
                        <ChevronRight size={24} />
                    </button>
                </>
            )}

            {/* Indicators */}
            {showIndicators && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {items.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${activeIndex === index ? "bg-white w-4" : "bg-white/50 hover:bg-white/80"
                                }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
